import { useState, useCallback, useRef, useEffect } from 'react';
import api from '../../api/client';

export default function StockAdjust({ product, onClose, onUpdated }) {
  const [quantity, setQuantity] = useState(
    product.stock_quantity != null ? product.stock_quantity : 0
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, []);

  const handleChange = useCallback((e) => {
    const val = parseInt(e.target.value, 10);
    setQuantity(isNaN(val) ? 0 : Math.max(0, val));
    setError('');
  }, []);

  const increment = useCallback(() => {
    setQuantity((q) => q + 1);
    setError('');
  }, []);

  const decrement = useCallback(() => {
    setQuantity((q) => Math.max(0, q - 1));
    setError('');
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const result = await api.updateStock(product.id, quantity);
      setSuccess(true);
      if (onUpdated) onUpdated(result);
      setTimeout(() => onClose(), 600);
    } catch (err) {
      setError(err?.message || 'Failed to update stock.');
    } finally {
      setSubmitting(false);
    }
  }, [product.id, quantity, onUpdated, onClose]);

  /**
   * Block onscan.js from intercepting keypresses on the number input.
   */
  const stopScanPropagation = useCallback((e) => {
    e.stopPropagation();
  }, []);

  function handleBackdropClick(e) {
    if (e.target === e.currentTarget) onClose();
  }

  const inputClasses =
    'block w-full rounded-md border border-pos-border bg-white px-3 py-2 text-sm text-pos-foreground ' +
    'text-center font-semibold text-lg outline-none transition-shadow ' +
    'focus:border-pos-primary focus:ring-2 focus:ring-pos-primary/20';

  const stepperBtn =
    'flex items-center justify-center w-10 h-10 rounded-lg border border-pos-border ' +
    'text-pos-foreground transition-colors hover:bg-pos-muted ' +
    'focus:outline-none focus:ring-2 focus:ring-pos-primary/30 ' +
    'disabled:opacity-40 disabled:cursor-not-allowed';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label="Adjust Stock"
    >
      <div className="relative w-full max-w-sm overflow-hidden rounded-xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-pos-border px-5 py-4">
          <h2 className="text-lg font-semibold text-pos-foreground">Adjust Stock</h2>
          <button
            type="button"
            onClick={onClose}
            className="flex-shrink-0 rounded-lg p-1.5 text-pos-muted-fg transition-colors hover:bg-pos-muted hover:text-pos-foreground focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
            aria-label="Close"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Error banner */}
        {error && (
          <div className="mx-5 mt-4 flex items-start gap-2 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700" role="alert">
            <svg className="h-4 w-4 mt-0.5 flex-shrink-0 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span>{error}</span>
          </div>
        )}

        {/* Body */}
        <form onSubmit={handleSubmit} noValidate>
          <div className="px-5 py-5 space-y-4">
            {/* Product info */}
            <div className="flex items-center gap-3">
              {product.image_url ? (
                <img
                  src={product.image_url}
                  alt={product.name}
                  className="w-12 h-12 rounded-lg object-cover border border-pos-border"
                />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center border border-pos-border">
                  <svg className="w-6 h-6 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21 15 16 10 5 21" />
                  </svg>
                </div>
              )}
              <div className="min-w-0">
                <p className="text-sm font-semibold text-pos-foreground truncate">{product.name}</p>
                {product.sku && (
                  <p className="text-xs text-pos-muted-fg">SKU: {product.sku}</p>
                )}
              </div>
            </div>

            {/* Current stock */}
            <div className="text-center">
              <span className="text-xs font-medium text-pos-muted-fg uppercase tracking-wide">
                Current stock
              </span>
              <p className="text-sm font-medium text-pos-foreground mt-0.5">
                {product.manage_stock
                  ? (product.stock_quantity != null ? product.stock_quantity : 'N/A')
                  : 'Not tracked'}
              </p>
            </div>

            {/* Stepper */}
            <div className="flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={decrement}
                disabled={quantity <= 0 || submitting}
                className={stepperBtn}
                aria-label="Decrease quantity"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              </button>

              <input
                ref={inputRef}
                type="number"
                min="0"
                step="1"
                value={quantity}
                onChange={handleChange}
                onKeyDown={stopScanPropagation}
                onKeyUp={stopScanPropagation}
                onKeyPress={stopScanPropagation}
                disabled={submitting}
                className={inputClasses + ' w-24'}
                aria-label="New stock quantity"
              />

              <button
                type="button"
                onClick={increment}
                disabled={submitting}
                className={stepperBtn}
                aria-label="Increase quantity"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 border-t border-pos-border px-5 py-4">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="rounded-lg border border-pos-border px-4 py-2 text-sm font-medium text-pos-foreground transition-colors hover:bg-pos-muted focus:outline-none focus:ring-2 focus:ring-pos-primary/20 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={submitting || success}
              className={
                'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white ' +
                'transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 ' +
                (success
                  ? 'bg-pos-success focus:ring-pos-success'
                  : submitting
                    ? 'bg-pos-primary/70 cursor-not-allowed focus:ring-pos-primary/40'
                    : 'bg-pos-primary hover:bg-pos-primary-dark focus:ring-pos-primary')
              }
            >
              {submitting && (
                <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              )}
              {success ? 'Updated' : submitting ? 'Updating...' : 'Update Stock'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
